import { ListingInfo, ListingStatus, queryGet } from '@apeiron/common'
import { CONTRACT, useApproveErc721, useContract } from '@apeiron/contract'
import { CurrencyTokenType } from '@apeiron/currency'
import { Planet, PlanetLite, PlanetStatus } from '@apeiron/planet'
import {
  useAccountContext,
  useChainSwitcher,
  useMessageContext,
  useRoleContext,
  useWeb3
} from '@apeiron/temp'
import { MuiLoading, usePath } from '@apeiron/ui'
import { Box, styled } from '@mui/material'
import EmporiumSecNavBar from '@src/components/navigationBar/secondary/EmporiumSecNavBar'
import { PlanetOnOfferSection } from '@src/components/planet/detail/actionViews/PlanetOnOfferSection'
import { PlanetOwnerSection } from '@src/components/planet/detail/actionViews/PlanetOwnerSection'
import PlanetDetailView from '@src/components/planet/detail/PlanetDetailView'
import PlanetCardImage from '@src/components/planet/share/planetCard/PlanetCardImage'
import ListEmptyState from '@src/components/share/list/ListEmptyState'
import TradeBuyDialog from '@src/components/share/trade/TradeBuyDialog'
import TradeGiftDialog from '@src/components/share/trade/TradeGiftDialog'
import TradeSellDialog from '@src/components/share/trade/TradeSellDialog'
import { PLANET_SALE_HISTORIES_FILTER } from '@src/constants/activity'
import { FeatureFlagType } from '@src/constants/featureFlag'
import { GAAssetType } from '@src/constants/googleAnalytics'
import { MORPH_PAGE_STATE } from '@src/constants/morph'
import { WhiteList } from '@src/constants/page'
import { ROUTE } from '@src/constants/route'
import { SaleMethodType } from '@src/constants/trade'
import {
  MorphProvider,
  useMorphContext
} from '@src/contexts/planet/MorphContext'
import { PlanetSkillsProvider } from '@src/contexts/planet/PlanetSkillsContext'
import {
  PlanetTypeProvider,
  usePlanetTypeContext
} from '@src/contexts/planet/PlanetTypeContext'
import { PrimevalLegacyProvider } from '@src/contexts/planet/PrimevalLegacyContext'
import { useSecondaryNavBarContext } from '@src/contexts/share/SecondaryNavBarContext'
import useTracking from '@src/hooks/common/analytics/useTracking'
import usePriceCalculation from '@src/hooks/common/tools/usePriceCalculation'
import useMarketplace from '@src/hooks/contract/useMarketplace'
import { useSafeTransferFrom } from '@src/hooks/contract/useSafeTransferFrom'
import useGetPlanet from '@src/hooks/graphql/useGetPlanet'
import { useGetPlanetActivities } from '@src/hooks/graphql/useGetPlanetActivities'
import useUpdatePlanetName from '@src/hooks/graphql/useUpdatePlanetName'
import useVerifyPlanet from '@src/hooks/graphql/useVerifyPlanet'
import { Core } from '@src/types/core'
import { SaleMethod, TradeItem } from '@src/types/trade'
import featureAllowed from '@src/util/apeiron/featureAllowed'
import { isCore, isListing } from '@src/util/apeiron/planet'
import { t } from 'i18next'
import type { NextPage } from 'next'
import { useRouter } from 'next/router'
import * as R from 'ramda'
import { FC, useCallback, useEffect, useState } from 'react'
import { Address, isAddress } from 'viem'

const StyledMuiLoading = styled(MuiLoading)`
  position: absolute;
  top: 50%;
  left: 50%;
`

const PlanetImageOnBuy = styled(PlanetCardImage)`
  width: 120px;
`

const NotFoundContainer = styled(Box)`
  margin: auto;
`

const Content: FC<Props> = () => {
  const router = useRouter()

  const { isCurrentPath } = usePath(WhiteList)

  const { getContractAddress } = useChainSwitcher()

  const planetId = queryGet<string>(router.query, 'planetId')

  const { account, isLoggedIn } = useAccountContext()

  const { toWei } = useWeb3()

  const { setupBar } = useSecondaryNavBarContext()

  const { getPlanetTypeData } = usePlanetTypeContext()

  const {
    data: planetData,
    fetch: fetchPlanet,
    loading: fetchPlanetLoading
  } = useGetPlanet()

  const { mutate: updatePlanetName } = useUpdatePlanetName()

  const { fetch: getPlanetActivities, data: planetActivities } =
    useGetPlanetActivities()

  const { morphedPlanet, pageState, canMorphCore, morphCore, clean } =
    useMorphContext()

  const { verify: verifyPlanet } = useVerifyPlanet()

  const approvePlanet = useApproveErc721(
    account.walletAddress as Address,
    getContractAddress(CONTRACT.PLANET.GET_USER_ALLOWANCE.address)
  )

  const transferContract = useContract<void>(CONTRACT.PLANET.SAFE_TRANSFER_FROM)

  const { transfer } = useSafeTransferFrom()

  const { buy, createListing, cancelListing, updateListing } = useMarketplace()

  const { checkRoleCanOperate } = useRoleContext()

  const { setInfo } = useMessageContext()

  const [planet, setPlanet] = useState<Planet | null>(null)

  const [enableMorph, setEnableMorph] = useState(false)

  const [isGiftDialogOpen, setIsGiftDialogOpen] = useState(false)

  const [isSellDialogOpen, setIsSellDialogOpen] = useState(false)

  const [isEditSellingMode, setIsEditSellingMode] = useState(false)

  const [isBuyDialogOpen, setIsBuyDialogOpen] = useState(false)

  const [isWaitingBuyApprove, setIsWaitingBuyApprove] = useState(false)

  const [isWaitingBuyConfirm, setIsWaitingBuyConfirm] = useState(false)

  const [isBought, setIsBought] = useState(false)

  const [isSellLoading, setIsSellLoading] = useState(false)

  const [isGiftLoading, setIsGiftLoading] = useState(false)

  const { ethPrice, usdPrice } = usePriceCalculation(planet?.listingInfo)

  const { gtmEmitPurchase, gtmEmitAddToFavourite, gtmEmitViewItem } =
    useTracking()

  const isOwner = planet?.owner.walletAddress === account.walletAddress

  const handleOnMorphClick = useCallback(async () => {
    if (R.isNotNil(planet) && R.isNotNil(planetId)) {
      const canMorph = await canMorphCore(planet as Core, true)

      if (canMorph) {
        const success = await morphCore(planet.planetID)

        if (success) {
          fetchPlanet({ planetId })
        }
      }
    }
  }, [planet, canMorphCore, morphCore, fetchPlanet, planetId])

  const handleOnRenderPlanetUrl = useCallback((planet: PlanetLite) => {
    return `${ROUTE.MARKETPLACE_PLANET_DETAIL}?planetId=${planet.planetID}`
  }, [])

  const handleOnNameUpdate = useCallback(
    async (name: string) => {
      if (planet) {
        const success = await updatePlanetName({
          planetID: planet.planetID,
          name
        })

        if (success) {
          setPlanet(R.assoc('name', name, planet))
        }
      }
    },
    [planet, updatePlanetName]
  )

  const handleOnBookmarkChange = useCallback(
    (bookmarked: boolean) => {
      const updatedPlanet = R.assoc('isBookmarked', bookmarked, planet)

      setPlanet(updatedPlanet as Planet)

      if (bookmarked) {
        // GA-Bpi3v1xK9nzMXWp
        gtmEmitAddToFavourite({
          type: GAAssetType.Planet,
          item: planet
        })
      }
    },
    [planet, gtmEmitAddToFavourite]
  )

  const onGiftClick = useCallback(() => {
    setIsGiftLoading(false)
    setIsGiftDialogOpen(true)
  }, [])

  const onGiftDialogClose = useCallback(() => {
    setIsGiftDialogOpen(false)
  }, [])

  const onSellClick = useCallback(() => {
    setIsSellLoading(false)
    setIsEditSellingMode(false)
    setIsSellDialogOpen(true)
  }, [])

  const onUpdateSellClick = useCallback(() => {
    setIsSellLoading(false)
    setIsEditSellingMode(true)
    setIsSellDialogOpen(true)
  }, [])

  const onSellDialogClose = useCallback(() => {
    setIsSellDialogOpen(false)
  }, [])

  const onBuyClick = useCallback(() => {
    setIsWaitingBuyApprove(false)
    setIsWaitingBuyConfirm(false)
    setIsBought(false)

    setIsBuyDialogOpen(true)
  }, [])

  const handleOnRenderImage = useCallback(() => {
    if (planet) {
      const planetTypeData = planet.planetType
        ? getPlanetTypeData(planet.planetType)
        : undefined

      return (
        <PlanetImageOnBuy
          coreType={planet.coreType}
          iconURL={planetTypeData?.planetTypeIcon}
          imageURL={planet.image ?? ''}
        />
      )
    }

    return <></>
  }, [getPlanetTypeData, planet])

  const handleOnPromptSuccess = useCallback(() => {
    setInfo({
      title: t('dialog.transaction_submit_success.content'),
      confirm: t('common.confirm'),
      onConfirm: () => {
        setInfo(null)
      }
    })
  }, [setInfo])

  const handleOnCancelSellSuccess = useCallback(() => {
    setInfo({
      title: t('dialog.sell_cancel_success.content'),
      confirm: t('common.confirm'),
      onConfirm: () => {
        setInfo(null)
      }
    })
  }, [setInfo])

  const OnBuyDialogClose = useCallback(() => {
    setIsBuyDialogOpen(false)
  }, [])

  const onSellDialogConfirm = useCallback(
    async (update: boolean, item: TradeItem, saleMethod: SaleMethod) => {
      try {
        const planet = item as Planet

        const { planetID } = planet

        const verify = (nftId: string): Promise<boolean> => {
          return verifyPlanet(
            nftId,
            update
              ? (verified: Planet) => {
                  const checkedFields: (keyof ListingInfo)[] = [
                    'duration',
                    'endPrice',
                    'startPrice'
                  ]

                  const newInfo =
                    verified.listingInfo &&
                    R.pick(checkedFields, verified.listingInfo)

                  const oldInfo =
                    planet.listingInfo &&
                    R.pick(checkedFields, planet.listingInfo)

                  return !R.equals(newInfo, oldInfo)
                }
              : (verified: Planet) =>
                  verified.nftListingStatus === ListingStatus.LISTING
          )
        }

        setIsSellLoading(true)

        const params = {
          contractParams: {
            nftAddress: getContractAddress(CONTRACT.PLANET._INFO.address),
            nftId: planetID,
            tokenAddress: getContractAddress(
              CONTRACT.CURRENCY.WETH._INFO.address
            ),

            startPrice: toWei(
              saleMethod.type === SaleMethodType.FixedPrice
                ? saleMethod.price.toString()
                : saleMethod.startPrice.toString()
            ),
            endPrice: toWei(
              saleMethod.type === SaleMethodType.FixedPrice
                ? saleMethod.price.toString()
                : saleMethod.endPrice.toString()
            ),
            duration:
              saleMethod.type === SaleMethodType.FixedPrice
                ? 0
                : saleMethod.duration
          },
          approve: approvePlanet,
          verify
        }

        const success: boolean = update
          ? await updateListing(params)
          : await createListing(params)

        setIsSellLoading(false)

        if (success) {
          onSellDialogClose()
          handleOnPromptSuccess()
          fetchPlanet({ planetId: planetID })
        }
      } catch (e) {
        setIsSellLoading(false)
      }
    },
    [
      approvePlanet,
      createListing,
      fetchPlanet,
      getContractAddress,
      toWei,
      handleOnPromptSuccess,
      onSellDialogClose,
      updateListing,
      verifyPlanet
    ]
  )

  const onGiftDialogConfirm = useCallback(
    async (gift: TradeItem, walletAddress: Address) => {
      setIsGiftLoading(true)

      const planet = gift as Planet

      const verify = (tokenId: string): Promise<boolean> => {
        return verifyPlanet(
          tokenId,
          (planet: Planet) =>
            R.pathOr('', ['owner', 'walletAddress'], planet) !==
            account.walletAddress
        )
      }

      if (!isAddress(account.walletAddress)) {
        throw new Error('Wallet address is not found')
      }

      const transferSuccess: boolean = await transfer({
        contract: transferContract,
        contractParams: {
          from: account.walletAddress,
          to: walletAddress,
          tokenId: planet.planetID
        },
        onVerify: verify
      })

      setIsGiftLoading(false)

      if (transferSuccess) {
        onGiftDialogClose()
        handleOnPromptSuccess()
        fetchPlanet({ planetId: planet.planetID })

        return true
      } else {
        return false
      }
    },
    [
      account.walletAddress,
      transferContract,
      fetchPlanet,
      handleOnPromptSuccess,
      onGiftDialogClose,
      transfer,
      verifyPlanet
    ]
  )

  const onBuyDialogConfirm = useCallback(
    async (
      item: TradeItem,
      price: number,
      settleOrderTokenType: CurrencyTokenType
    ): Promise<boolean> => {
      if (checkRoleCanOperate()) {
        const planet = item as Planet

        if (planet.listingInfo) {
          setIsWaitingBuyApprove(true)

          const onApproved = () => {
            setIsWaitingBuyApprove(false)
            setIsWaitingBuyConfirm(true)
          }

          const onBought = () => {
            setIsWaitingBuyConfirm(false)
            setIsBought(true)
            setIsBuyDialogOpen(false)
            handleOnPromptSuccess()
            fetchPlanet({ planetId: planet.planetID })
            // GA-nSDjhAEBSW3L2Vi
            gtmEmitPurchase({
              type: GAAssetType.Planet,
              item: planet
            })
          }

          const onFailed = () => {
            setIsWaitingBuyApprove(false)
            setIsWaitingBuyConfirm(false)
            setIsBought(false)
          }

          const verify = (tokenId: string): Promise<boolean> => {
            return verifyPlanet(
              tokenId,
              (planet: Planet) =>
                planet.owner.walletAddress === account.walletAddress
            )
          }

          return buy({
            contractParams: {
              ownerAddress: planet.owner.walletAddress,
              listAt: planet.listingInfo.startDate.toString(),
              nftId: planet.planetID,
              nftAddress: getContractAddress(CONTRACT.PLANET._INFO.address),
              roninSaleOrder: planet.listingInfo.roninSaleOrder,
              settleOrderTokenType,
              price
            },
            onApproved,
            onBought,
            onFailed,
            verify
          })
        }

        return false
      } else {
        OnBuyDialogClose()

        return false
      }
    },
    [
      account.walletAddress,
      buy,
      checkRoleCanOperate,
      fetchPlanet,
      getContractAddress,
      gtmEmitPurchase,
      handleOnPromptSuccess,
      OnBuyDialogClose,
      verifyPlanet
    ]
  )

  const onCancelSell = useCallback(
    async (planetId: string, listingInfo: ListingInfo) => {
      const verify = (tokenId: string): Promise<boolean> => {
        return verifyPlanet(
          tokenId,
          (planet: Planet) =>
            planet.nftListingStatus === ListingStatus.UNLISTING
        )
      }

      const success = await cancelListing({
        contractParams: {
          nftAddress: getContractAddress(CONTRACT.PLANET._INFO.address),
          nftId: planetId,
          roninSaleOrder: listingInfo?.roninSaleOrder
        },
        verify
      })

      if (success) {
        handleOnCancelSellSuccess()
        fetchPlanet({ planetId })
      }
    },
    [
      cancelListing,
      fetchPlanet,
      getContractAddress,
      handleOnCancelSellSuccess,
      verifyPlanet
    ]
  )

  const handleOnRenderMarketSection = useCallback(
    (planet: Planet) => {
      const isListingPlanet = isListing(planet)

      const children = []

      if (isListingPlanet) {
        children.push(
          <PlanetOnOfferSection
            key='offer_section'
            listingInfo={planet.listingInfo}
            planetId={planet.planetID}
            priceViewProps={{
              price: ethPrice,
              priceFiat: usdPrice
            }}
            onBuy={onBuyClick}
            shouldShowBuy={!isOwner}
            onCancelSell={onCancelSell}
            onUpdateSell={onUpdateSellClick}
            shouldShowCancelSell={isOwner}
          />
        )
      }

      if (isLoggedIn && isOwner && planet.status === PlanetStatus.Enable) {
        children.push(
          <PlanetOwnerSection
            key='owner_section'
            shouldShowMorph={isCore(planet) && !isListing(planet)}
            shouldShowSell={!isListingPlanet}
            shouldShowGift={!isListingPlanet}
            isMorphEnabled={enableMorph}
            onGiftClick={onGiftClick}
            onSellClick={onSellClick}
            onMorphClick={handleOnMorphClick}
          />
        )
      }

      return children
    },
    [
      enableMorph,
      ethPrice,
      handleOnMorphClick,
      isLoggedIn,
      isOwner,
      onBuyClick,
      onCancelSell,
      onGiftClick,
      onSellClick,
      onUpdateSellClick,
      usdPrice
    ]
  )

  useEffect(() => {
    if (R.isNotNil(planetId)) {
      fetchPlanet({ planetId }).then(planet => {
        if (planet) {
          // GA-qw3h75xu6e2YPSE
          gtmEmitViewItem({
            type: GAAssetType.Planet,
            item: planet
          })
        }
      })
    }
  }, [planetId])

  useEffect(() => {
    if (planet) {
      const checkEnableMorph = async () => {
        const enabled = await canMorphCore(planet)

        setEnableMorph(enabled)
      }

      checkEnableMorph()
    }
  }, [planet])

  useEffect(() => {
    if (pageState === MORPH_PAGE_STATE.REFETCH_DETAIL && morphedPlanet) {
      setPlanet(morphedPlanet)
      clean()
    }
  }, [clean, morphedPlanet, pageState])

  useEffect(() => {
    if (planetData) {
      setPlanet(planetData)
    }
  }, [planetData])

  useEffect(() => {
    if (planet?.planetID) {
      getPlanetActivities({
        filterInput: { planetID: [planet.planetID] },
        activityType: PLANET_SALE_HISTORIES_FILTER,
        skipTotal: true
      })
    }
  }, [planet?.planetID, planet?.owner.walletAddress])

  useEffect(() => {
    if (isCurrentPath(ROUTE.MARKETPLACE_PLANET_DETAIL, [])) {
      setupBar(<EmporiumSecNavBar index={0} />)

      return () => setupBar(null)
    }
  }, [setupBar])

  return (
    <>
      {fetchPlanetLoading ? (
        <StyledMuiLoading loading />
      ) : R.isNil(planet) ? (
        <NotFoundContainer>
          <ListEmptyState description={t('planet.detail.not_found')} />
        </NotFoundContainer>
      ) : (
        <>
          <PlanetDetailView
            isEditable={isOwner}
            planet={planet}
            renderPlanetUrl={handleOnRenderPlanetUrl}
            shouldShowRelic={featureAllowed(FeatureFlagType.ShowPlanetRelic)}
            renderMarketSection={handleOnRenderMarketSection}
            isBookmarked={planet.isBookmarked}
            onNameUpdate={handleOnNameUpdate}
            onBookmarkChange={handleOnBookmarkChange}
            shouldShowStats={featureAllowed(FeatureFlagType.ShowPlanetStat)}
            shouldShowSkill
            saleHistories={planetActivities}
          />
          <TradeGiftDialog
            checkboxRemark={t('dialog.gift_planet.checkbox_description')}
            description={t('dialog.gift_planet.description', {
              planetName: planet.name
            })}
            loading={isGiftLoading}
            isOpened={isGiftDialogOpen}
            item={planet}
            title={t('dialog.gift_planet.title')}
            onClose={onGiftDialogClose}
            onConfirm={onGiftDialogConfirm}
          />
          <TradeSellDialog
            description={t('dialog.sell_planet.description', {
              planetName: planet.name
            })}
            loading={isSellLoading}
            isOpened={isSellDialogOpen}
            isEditMode={isEditSellingMode}
            item={planet}
            title={
              planet.listingInfo
                ? t('dialog.sell_planet.update_title')
                : t('dialog.sell_planet.title')
            }
            onClose={onSellDialogClose}
            onConfirm={onSellDialogConfirm}
          />
          {planet.listingInfo && (
            <TradeBuyDialog
              item={planet}
              isOpened={isBuyDialogOpen}
              isWaitingApprove={isWaitingBuyApprove}
              isWaitingConfirm={isWaitingBuyConfirm}
              isBought={isBought}
              itemName={planet.name}
              listingInfo={planet.listingInfo}
              renderImage={handleOnRenderImage}
              onClose={OnBuyDialogClose}
              onConfirm={onBuyDialogConfirm}
            />
          )}
        </>
      )}
    </>
  )
}

const PlanetDetailPage: NextPage<Props> = (props: Props) => {
  return (
    <PlanetTypeProvider>
      <PrimevalLegacyProvider>
        <PlanetSkillsProvider>
          <MorphProvider>
            <Content {...props} />
          </MorphProvider>
        </PlanetSkillsProvider>
      </PrimevalLegacyProvider>
    </PlanetTypeProvider>
  )
}

type Props = {}

export default PlanetDetailPage
